"use client"

import { useState, useEffect, useRef } from "react"
import { useRouter } from "next/navigation"
import { Search, Clock, TrendingUp, Loader2 } from "lucide-react"
import { searchProducts } from "@/lib/utils"

const popularSearches = ["Pain relief", "Vitamins", "Blood pressure", "Skincare", "Baby care", "First aid"]

interface SearchResult {
  id: number
  name?: string
  product_name?: string
  code?: string
  product_code?: string
  category?: string
  categ?: string
  brand?: string
  brand_id?: number
  price?: number
  selling_price?: number
  cost_price?: number
  image?: string
  short_description?: string
  in_stock?: boolean
  stock_available?: number
}

export default function SearchDropdown() {
  const router = useRouter()
  const [query, setQuery] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const searchRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Load recent searches from localStorage on component mount
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (error) {
        console.error('Failed to parse recent searches:', error)
      }
    }
  }, [])

  // Debounced search function
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (query.length >= 2) {
      setLoading(true)
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const results = await searchProducts(query)
          console.log('Search results:', results)
          // Handle both array response and object with data property
          const searchData = Array.isArray(results) ? results : (results.data || [])
          console.log('Processed search data:', searchData)
          setSearchResults(searchData)
        } catch (error) {
          console.error('Search failed:', error)
          setSearchResults([])
        } finally {
          setLoading(false)
        }
      }, 300) // 300ms delay
    } else {
      setSearchResults([])
      setLoading(false)
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [query])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) return

    // Add to recent searches
    const updatedRecentSearches = [
      searchTerm,
      ...recentSearches.filter(item => item !== searchTerm)
    ].slice(0, 5) // Keep only last 5 searches

    setRecentSearches(updatedRecentSearches)
    localStorage.setItem('recentSearches', JSON.stringify(updatedRecentSearches))

    // Navigate to shop page with search query
    router.push(`/shop?search=${encodeURIComponent(searchTerm)}`)
  }

  const handleProductClick = (product: SearchResult) => {
    // Close dropdown
    setIsOpen(false)
    setQuery("")
    
    // Navigate to product detail page
    router.push(`/product/${product.id}`)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(query)
    }
  }

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <input
          type="search"
          placeholder="Search for medicines, health products..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyPress={handleKeyPress}
          className="w-full pl-4 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
        />
        <button
          onClick={() => handleSearch(query)}
          className="absolute right-1 top-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 mt-1 max-h-96 overflow-y-auto">
          {query.length > 0 ? (
            <div>
              {loading ? (
                <div className="p-4 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                  <p className="text-sm text-gray-500 mt-2">Searching...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2">
                    Products ({searchResults.length})
                  </div>
                  {searchResults.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleProductClick(item)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-left"
                    >
                      <Search className="w-4 h-4 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {item.name || item.product_name || item.product_code || item.code}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {item.category || item.categ || 'Product'}
                          {item.brand && ` • ${item.brand}`}
                        </div>
                        {(item.selling_price || item.price) && (
                          <div className="text-xs text-green-600 font-medium">
                            KSh {Number(item.selling_price || item.price).toLocaleString()}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : query.length >= 2 ? (
                <div className="p-4 text-center text-gray-500">
                  No products found for "{query}"
                </div>
              ) : (
                <div className="p-4 text-center text-gray-500">
                  Type at least 2 characters to search
                </div>
              )}
            </div>
          ) : (
            <div className="p-2">
              {recentSearches.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2 flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    Recent Searches
                  </div>
                  {recentSearches.map((search, index) => (
                    <button
                      key={index}
                      onClick={() => handleSearch(search)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-left"
                    >
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{search}</span>
                    </button>
                  ))}
                </div>
              )}

              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-2 flex items-center gap-2">
                  <TrendingUp className="w-3 h-3" />
                  Popular Searches
                </div>
                {popularSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => handleSearch(search)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md text-left"
                  >
                    <TrendingUp className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-700">{search}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
